import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';
import { createActionState, formValueMap } from '@at/shared';
import { DateInterval, Rsvp, RsvpStatus } from '../../entities';
import { DatesApi, PostRsvpRequest } from '../../api';
import { DatesService } from '../../services/dates.service';
import { FormFieldComponent } from '../../../../../shared/components/form-field/form-field.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CURRENT_TRIP } from '../../../../services';
import { isTripOwner } from '../../../../shared';

@Component({
  selector: 'at-rsvp',
  standalone: true,
  imports: [DatePipe, FormFieldComponent, ReactiveFormsModule],
  templateUrl: './rsvp.component.html',
  styleUrl: './rsvp.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RsvpComponent {
  readonly #datesApi = inject(DatesApi);
  readonly #datesService = inject(DatesService);
  readonly #tripState = inject(CURRENT_TRIP);

  public dates = input.required<DateInterval | undefined>();
  public replyChange = output<RsvpStatus | undefined>();
  public previousReply = input<Rsvp>();

  protected readonly tripTitle = computed(() => this.#tripState.trip()?.title);
  protected readonly isOwner = isTripOwner();
  protected rsvpForm = new FormGroup({
    status: new FormControl<RsvpStatus | null>(null, Validators.required),
    message: new FormControl(''),
  });
  protected rsvp = new Subject<RsvpStatus>();
  protected state = toSignal(
    createActionState({
      trigger: this.rsvp.asObservable().pipe(
        formValueMap(this.rsvpForm),
        filter((request): request is PostRsvpRequest => request.status !== null),
      ),
      apiCall: (request) => this.#datesApi.postRsvp(request),
      onSucceed: (_, request) => {
        this.#datesService.updateDatesSummary();
        this.onReplyChange(request.status);
      },
    }),
  );

  protected onReplyChange(response?: RsvpStatus) {
    this.replyChange.emit(response);
  }

  constructor() {
    effect(() => {
      const prevReply = this.previousReply();
      if (!prevReply) return;
      this.rsvpForm.patchValue(prevReply);
    });
  }
}
